import React from 'react';
import ReactDOM from 'react-dom';
import '../styles/app/badges.css';

// Metal tier for a badge's medallion frame. Belts and titles are belt gold;
// commissioner tiers follow their artwork; perfect-pick badges are win green.
export const badgeTier = (badge) => {
  if (!badge) return 'red';
  if (badge.type === 'title_shot_winner' || badge.type === 'white_house_belt' || badge.rare) return 'gold';
  const icon = badge.icon || '';
  if (icon.includes('-diamond')) return 'diamond';
  if (icon.includes('-gold')) return 'gold';
  if (icon.includes('-silver')) return 'silver';
  if (icon.includes('-bronze')) return 'bronze';
  if ((badge.type || '').startsWith('perfect_')) return 'green';
  return 'red';
};

// Progress toward a locked milestone badge, when the stats we already have
// cover it (picks made / events picked). Returns null otherwise.
export const getBadgeProgress = (badge, stats) => {
  if (!badge || !stats) return null;
  const m = /^(picks_made|events_picked)_(\d+)$/.exec(badge.type || '');
  if (!m) return null;
  const current = m[1] === 'picks_made' ? stats.totalPicks : stats.eventsPicked;
  if (typeof current !== 'number') return null;
  const target = Number(m[2]);
  return { current: Math.min(current, target), target, pct: Math.max(0, Math.min(100, Math.round((current / target) * 100))) };
};

const LockGlyph = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="5" y="10.5" width="14" height="10" rx="2.5" fill="currentColor" />
    <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
  </svg>
);

// Collectible octagon medallion around a badge's SVG artwork.
export function BadgeMedallion({ badge, earned, count = 0, size = 72, showLock = true, className = '' }) {
  if (!badge) return null;
  const tier = badgeTier(badge);
  return (
    <span
      className={`bdg-medal bdg-tier-${tier} ${earned ? 'bdg-earned' : 'bdg-locked'} ${className}`}
      style={{ '--bdg-size': `${size}px` }}
    >
      <span className="bdg-frame">
        <span className="bdg-core">
          <img className="bdg-img" src={badge.icon} alt={badge.label} draggable="false" />
        </span>
      </span>
      {!earned && showLock && (
        <span className="bdg-lock" aria-hidden="true"><LockGlyph /></span>
      )}
      {earned && count > 1 && (
        <span className="bdg-count">×{count}</span>
      )}
    </span>
  );
}

export default function BadgeDetailModal({ badge, earned, count, onClose, progress }) {
  if (!badge) return null;
  const tier = badgeTier(badge);

  // Portaled to <body> so it stacks above the floating dock (page wrappers
  // animate opacity, which would otherwise trap it in their stacking context).
  return ReactDOM.createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={badge.label}
      onClick={onClose}
      className="bdg-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`bdg-modal bdg-modal-${tier} ${earned ? 'bdg-modal-earned' : 'bdg-modal-locked'}`}
      >
        <div className="bdg-modal-glow" aria-hidden="true" />

        {/* Close X */}
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="bdg-modal-close"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
          </svg>
        </button>

        {/* Badge medallion */}
        <div className="bdg-modal-medal">
          <BadgeMedallion badge={badge} earned={earned} count={earned && badge.multi ? count : 0} size={148} />
        </div>

        {/* Title */}
        <h3 className="bdg-modal-title">
          {badge.label}
        </h3>

        {/* Earned status pill */}
        <div className="bdg-modal-status">
          {earned ? (
            <span className={`pnp-chip ${tier === 'gold' ? 'pnp-chip-gold' : 'pnp-chip-green'}`}>
              Earned{badge.multi && count > 1 ? ` · ×${count}` : ''}
            </span>
          ) : (
            <span className="pnp-chip pnp-chip-mute">
              Locked
            </span>
          )}
        </div>

        {/* Description */}
        {badge.description && (
          <p className="bdg-modal-desc">
            {badge.description}
          </p>
        )}

        {/* How to earn */}
        {!earned && badge.criteria && (
          <div className="bdg-modal-earn">
            <div className="bdg-modal-earn-label">
              How to earn
            </div>
            <div className="bdg-modal-earn-text">
              {badge.criteria}
            </div>
            {progress && (
              <div className="bdg-progress bdg-modal-progress">
                <div className="bdg-progress-track">
                  <div className="bdg-progress-fill" style={{ width: `${progress.pct}%` }} />
                </div>
                <div className="bdg-progress-num">
                  {progress.current.toLocaleString()} / {progress.target.toLocaleString()}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
